import React, { FC, memo, useMemo, useContext, ReactNode } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import TransclusionContext from '../../../components/transclusion/_TransclusionContext';
import Transclude from '../../../components/transclusion/_Transclude';
import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';

export interface WithTransclusionProps {
  children?: ReactNode | ((renderProps: object) => ReactNode);
}

const withTransclusion = (WrappedComponent: FC<WithTransclusionProps>) => {
  const MemoizedWrappedComponent = memo(WrappedComponent);
  const transclude = <Transclude />;

  const WithTransclusion = ({ children, ...props }: WithTransclusionProps) => {
    const transclusionContext = useContext(TransclusionContext);

    const newTransclusions = useMemo(() => {
      const { transclusions = [] } = transclusionContext;
      return (
        [...transclusions, { children }]
      );
    }, [transclusionContext, children]);

    const isRenderProps = useMemo(() => typeof children === 'function', [children]);

    const placeholder = useMemo(() => (
      isRenderProps ?
        (renderProps: object) => <Transclude {...renderProps} /> :
        transclude
    ), [isRenderProps]);

    const newContext = useMemo(() => ({ transclusions: newTransclusions }), [newTransclusions]);

    return (
      <TransclusionContext.Provider value={newContext}>
        <MemoizedWrappedComponent {...props}>
          {placeholder}
        </MemoizedWrappedComponent>
      </TransclusionContext.Provider>
    );
  };

  const MemoizedWithTransclusion = hoistNonReactStatics(memo(WithTransclusion), WrappedComponent);

  MemoizedWithTransclusion.displayName = getWrappedDisplayName('WithTransclusion', WrappedComponent);

  return MemoizedWithTransclusion;
};

export default withTransclusion;
